import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';

export namespace OnlyFansInit {
    export interface Init {
        pwaPublicKey: string;
        postMediaConfig: PostMediaConfig;
        personalConfig: PersonalConfig;
        settings: Settings;
        paymentGatesEnabledForCardAdd: string[];
        cardsApiUrl: string;
        cards: Cards;
        allowedMessages: string[];
        tipsMin: number;
        tipsMinInternal: number;
        addCardFrameUrl: string;
        isScanCardEnabled: boolean;
        isPostsViewsEnabled: boolean;
        isProfilesViewsEnabled: boolean;
        isProfilesVisitsEnabled: boolean;
        isClicksStatsEnabled: boolean;
        faceIdAvailable: boolean;
        cardVerification: CardVerification;
        socialMediaProviders: SocialMediaProvider[];
        usersLinksMaxLimit: number;
        socialButtonsMaxLimit: number;
        upload: Upload;
        recaptchaEPublic: string;
        recaptchaECPublic: string;
        turnstile: Turnstile;
        isTwitterManualPostingEnabled: boolean;
        captcha: string[];
    }

    export interface CardVerification {
        maxAmount: number;
        minAmount: number;
    }

    export interface Cards {
        securionpay: Securionpay;
        securionpayWl: SecurionpayWl;
        inovio: Inovio;
    }

    export interface Inovio {
        proxyHost: null;
    }

    export interface Securionpay {
        publicKeys: PublicKey[];
    }

    export interface PublicKey {
        publicKey: string;
        first?: Array<number[] | number>;
        users?: User[];
    }

    export interface User {
        comparison: string;
        id: number;
    }

    export interface SecurionpayWl {
        publicKey: string;
    }

    export interface PersonalConfig {
        prefix: string;
        avatar: Avatar;
        header: Avatar;
        max_pinned_posts_count: number;
    }

    export interface Avatar {
        minWidth: number;
        minHeight: number;
        min_width: number;
        min_height: number;
        max_width: number;
        max_height: number;
    }

    export interface PostMediaConfig {
        types: string[];
        ext: EXT;
        count: Count;
        params: Params;
        prefix: string;
        thumbs_prefix: string;
        geo_converter: GeoConverter;
        extensions: Extensions;
    }

    export interface Count {
        gif: number;
        photo: number;
        video: number;
        stream: number;
        audio: number;
    }

    export interface EXT {
        gif: string;
        photo: string;
        banking: string;
        vat_doc: string;
        video: string;
        stream: string;
        audio: string;
        admin_tickets_requests: string;
        rf: string;
        profile_links: string;
    }

    export interface Extensions {
        gif: string[];
        photo: string[];
        banking: string[];
        vat_doc: string[];
        video: string[];
        stream: string[];
        audio: string[];
        admin_tickets_requests: string[];
        rf: string[];
        profile_links: string[];
    }

    export interface GeoConverter {
        hosts: string[];
        proto: string;
        path: string;
        args: Args;
        types: string[];
    }

    export interface Args {
        preset: string;
        protected_preset: string;
        preset_png: string;
        isDelay: boolean;
        needThumbs: boolean;
    }

    export interface Params {
        photo: Photo;
        banking: Banking;
        vat_doc: Banking;
        rf: Banking;
    }

    export interface Banking {
        max_file_size: number;
        min_file_size?: number;
        max_width: number;
        max_height: number;
        min_width: number;
        min_height: number;
        min_dpi: number;
        min_format_size?: MinFormatSize;
    }

    export interface MinFormatSize {
        x: number;
        y: number;
    }

    export interface Photo {
        max_width: number;
        max_height: number;
        min_width: number;
        min_height: number;
    }

    export interface Settings {
        isImageWatermarkNeeded: boolean;
        userLoginPrefix: string;
        subscribeMinPrice: number;
        subscribeMaxPrice: number;
    }

    export interface SocialMediaProvider {
        socialMedia: string;
        label: string;
        isLink: boolean;
        isShowAt: boolean;
    }

    export interface Turnstile {
        managed: string;
        invisible: string;
    }

    export interface Upload {
        geoUploadTypes: string[];
        geoUploadHosts: GeoUploadHost[];
        s3: S3;
    }

    export interface GeoUploadHost {
        host: string;
        url: string;
    }

    export interface S3 {
        uploadPath: string;
        secureUploadPath: string;
    }
}

const useSubscriberInit = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();

    const [allowedUploadTypes, setAllowedUploadTypes] = useState<string[]>([]);

    // Fetch Subscriber's Lists
    const fetchData = async (): Promise<{ init: OnlyFansInit.Init | null; isLoggedIn: boolean }> => {
        const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/init`;

        return axios
            .get(query)
            .then(response => {
                if (response.status === 200) {
                    const initData: { init: OnlyFansInit.Init; isLoggedIn: boolean } = response.data;

                    // For each extension type, find the strings with "/" and flatten them into allowedUploadTypes
                    const extensions = initData?.init?.postMediaConfig?.extensions || {};

                    const types: string[] = [];

                    Object.keys(extensions).forEach(key => {
                        extensions[key as keyof OnlyFansInit.Extensions].forEach(ext => {
                            if (ext.includes('/')) {
                                types.push(ext);
                            }
                        });
                    });

                    setAllowedUploadTypes(types);

                    return initData;
                } else {
                    console.error('Failed to fetch OnlyFans Init Data:', response);

                    return { init: null, isLoggedIn: false };
                }
            })
            .catch(error => {
                console.error('Error fetching OnlyFans Init Data:', error);

                return { init: null, isLoggedIn: false };
            });
    };

    const subscriberInit = useQuery(['subscriberInit', params.userId], () => fetchData(), {
        refetchOnWindowFocus: false,
        retry: false,
        enabled: userContext.jwtToken && settingsContext.apiKey && params.userId ? true : false,
    });

    return { ...subscriberInit, allowedUploadTypes };
};

export default useSubscriberInit;
